// Shared demo-reset logic (one-click "Restore the demo world").
//
// The pristine demo reset is exposed from more than one surface (the topbar
// Workspace menu and the Help Center "Start fresh" card). This module owns the
// SINGLE implementation of that flow — eligibility gate, confirm-dialog state,
// the POST, toasts, and reload — so every surface stays in exact copy and
// behavior lockstep.
//
// Gating is server-flag driven (GET /api/config/client → flags
// .destructiveDemoResetEnabled) plus a role check. Operator mode is NOT
// required for this control.

import { useCallback, useState } from 'react';
import { rbac } from '@dha-nexus/shared';
import { api } from '../../lib/apiClient.js';
import { toast, activityRail } from '../../components/index.js';
import { useClientConfig } from '../../lib/clientConfig.js';
import { useCurrentUser } from '../../app/CurrentUserContext.jsx';

const RESEED_SUCCESS_KEY = 'dha_prime__demo_reseed_success';
const RESEED_TARGET_KEY = 'dha_prime__demo_reseed_target';

// Roles that may trigger a demo reset. Pending Access (no roles) is excluded;
// SYSTEM_ADMIN always passes via rbac.hasAnyRole.
export const DEMO_RESET_ELIGIBLE_ROLES = Object.freeze([
  rbac.ROLE_IDS.EXECUTIVE_SPONSOR,
  rbac.ROLE_IDS.PORTFOLIO_MANAGER,
  rbac.ROLE_IDS.PROGRAM_MANAGER,
  rbac.ROLE_IDS.PROJECT_MANAGER,
  rbac.ROLE_IDS.SYSTEM_ADMIN,
  rbac.ROLE_IDS.DATA_STEWARD,
]);

const SHARED_DEMO_RESET_COPY = Object.freeze({
  menuLabel: 'Restore the demo world',
  menuSummary: "Put every page back to the demo's starting point.",
  confirmTitle: 'Restore the demo world?',
  confirmBody:
    "This puts every page back to the demo's starting point. Anyone else using the site right now will lose their in-progress demo work. It takes about a minute, then the page reloads.",
  confirmGo: 'Restore the demo world',
  restoringTitle: 'Restoring the demo world…',
  restoringBody: 'Restoring the demo world. Keep this window open until the current page reloads.',
  restoringGo: 'Restoring…',
  helpCardTitle: 'Start fresh',
  helpCardBody:
    'Made a mess? Restore the demo world to its starting point so you can run the walkthrough again.',
  helpCardCta: 'Restore the demo world',
  inProgress409: 'A restore is already running. Wait a moment and refresh.',
  successTitle: 'Demo world restored',
  successSummary: 'Demo world restored',
  errorTitle: 'Restore the demo world failed',
  ariaLabel: 'Confirm restore the demo world',
});

const PRIVATE_SANDBOX_RESET_COPY = Object.freeze({
  menuLabel: 'Reset this sandbox',
  menuSummary: 'Restore only this private UAT sandbox to its starting point.',
  confirmTitle: 'Reset this sandbox?',
  confirmBody:
    'This restores only your private sandbox to its starting point. Records created during your run are replaced by the sandbox baseline, then the page reloads.',
  confirmGo: 'Reset this sandbox',
  restoringTitle: 'Resetting this sandbox…',
  restoringBody: 'Resetting your private sandbox. Keep this window open until the current page reloads.',
  restoringGo: 'Resetting…',
  helpCardTitle: 'Reset this sandbox',
  helpCardBody:
    'Need a clean rerun? Restore only this private sandbox to its starting point without affecting any other tester.',
  helpCardCta: 'Reset this sandbox',
  inProgress409: 'A sandbox reset is already running. Wait a moment and refresh.',
  successTitle: 'Sandbox restored',
  successSummary: 'Private sandbox restored',
  errorTitle: 'Reset this sandbox failed',
  ariaLabel: 'Confirm reset this sandbox',
});

export function getDemoResetCopy(clientConfig) {
  if (clientConfig?.sandbox?.mode === 'sandbox') {
    return PRIVATE_SANDBOX_RESET_COPY;
  }
  return SHARED_DEMO_RESET_COPY;
}

export function useDemoResetCopy() {
  const clientConfig = useClientConfig();
  return getDemoResetCopy(clientConfig);
}

/**
 * Whether the current actor may see/trigger the demo reset: server flag on AND
 * signed-in AND holding a reset-eligible role.
 *
 * @returns {boolean}
 */
export function useDemoResetEligible() {
  const { user, roles } = useCurrentUser();
  const config = useClientConfig();
  const flagOn = Boolean(config?.flags?.destructiveDemoResetEnabled);
  const isFrontDoor = config?.sandbox?.mode === 'front_door';
  return (
    flagOn &&
    !isFrontDoor &&
    Boolean(user?.id) &&
    rbac.hasAnyRole(roles, DEMO_RESET_ELIGIBLE_ROLES)
  );
}

/**
 * The demo-reset state machine + action. Shared by the topbar and Help Center.
 *
 * @returns {{
 *   confirmOpen: boolean,
 *   resetting: boolean,
 *   requestReset: () => void,
 *   cancelReset: () => void,
 *   performReset: () => Promise<void>,
 * }}
 */
export function useDemoReset() {
  const { user } = useCurrentUser();
  const clientConfig = useClientConfig();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const copy = getDemoResetCopy(clientConfig);

  const requestReset = useCallback(() => setConfirmOpen(true), []);
  const cancelReset = useCallback(() => {
    setResetting((busy) => {
      if (!busy) setConfirmOpen(false);
      return busy;
    });
  }, []);

  const performReset = useCallback(async () => {
    setResetting(true);
    const stickyId = toast.show({
      title: copy.restoringTitle,
      body: copy.restoringBody,
      tone: 'info',
      durationMs: 60000,
    });
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    try {
      const res = await api.post('/admin/seed/reset', {});
      toast.dismiss?.(stickyId);
      const baselineHash =
        res?.data?.baselineHash || res?.data?.baseline_hash || res?.baselineHash || '';
      toast.success({
        title: copy.successTitle,
        body: `Baseline hash ${String(baselineHash).slice(0, 8) || 'unknown'}`,
      });
      activityRail.push({
        action: 'admin.seed_reset',
        targetType: clientConfig?.sandbox?.mode === 'sandbox' ? 'sandbox' : 'demo',
        targetId: 'seed',
        actorUserId: user?.id || 'unknown',
        summary: copy.successSummary,
      });
      try {
        window.sessionStorage.setItem(
          RESEED_SUCCESS_KEY,
          JSON.stringify({ baselineHash, restoredAt: new Date().toISOString(), returnTo }),
        );
        window.sessionStorage.setItem(RESEED_TARGET_KEY, returnTo);
      } catch {
        /* session storage may be unavailable; reload still restores data */
      }
      setConfirmOpen(false);
      window.location.assign(returnTo || '/home');
    } catch (e) {
      toast.dismiss?.(stickyId);
      if (e?.status === 409 || e?.code === 'RESET_IN_PROGRESS') {
        // Another operator's restore is already running — friendly, non-reload.
        toast.error({ title: 'Restore already running', body: copy.inProgress409 });
      } else {
        toast.error({ title: copy.errorTitle, body: e.message });
      }
    } finally {
      setResetting(false);
    }
  }, [clientConfig?.sandbox?.mode, copy, user?.id]);

  return { confirmOpen, resetting, requestReset, cancelReset, performReset, copy };
}
